import { Component, OnInit, Input, Self, Optional, ViewChild, Output, EventEmitter } from '@angular/core';
import { NgControl, ControlValueAccessor, FormControl } from '@angular/forms';
import { IAddress } from '@bciti/types';
import { Observable, of } from 'rxjs';
import { GeoidService } from '../geoid-address/service/geoid.abstract.service';
import { debounceTime, distinctUntilChanged, filter, startWith, switchMap } from 'rxjs/operators';
import { Address } from 'src/app/user/user.model';

@Component({
    selector: 'app-address-autocomplete',
    templateUrl: './address-autocomplete.component.html',
    styleUrls: ['./address-autocomplete.component.scss'],
})
export class AddressAutocompleteComponent implements OnInit, ControlValueAccessor {
    public onChange: (x?: any) => void;

    public onTouched: (x?: any) => void;

    public id: string;

    public _value: IAddress | undefined;

    @Input() disabled: boolean = false;
    @Input() label: string | undefined;
    @Input() placeholder: string = '';
    @Input() displayClearButton: boolean = true;

    myControl = new FormControl();
    filteredOptions: Observable<Address[]>;

    @Input() validation = true;

    @Input() disableMessages = false;

    @Output('change') changeOutput = new EventEmitter<any>();

    @ViewChild('input', { static: true }) input;

    constructor(
        @Self() @Optional() private ngControl: NgControl,
        private geoidService: GeoidService,
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }

        this.filteredOptions = this.myControl.valueChanges.pipe(
            startWith(''),
            filter((value) => typeof value === 'string' && value.trim().length > 3),
            debounceTime(700),
            distinctUntilChanged(),
            switchMap((value: string) => this.geoidService.searchAddressQuery(value)),
        );
    }

    optionsSelected(item: Address) {
        this.value = item;
    }

    concatAddress(address: Address | undefined): string {
        if (!address) {
            return '';
        }

        return `${address.number} ${address.streetName} ${address.city} ${address.province} ${address.postCode}`;
    }

    ngOnInit(): void {
        if (!!this.value) {
            this.myControl.setValue(this.value);
        }
    }

    clear() {
        this.value = undefined;
    }

    get value(): any {
        return this._value;
    }

    set value(v) {
        this._value = v;
        if (this.onChange) {
            this.changeOutput.emit(v);
            this.onChange(v);
            this.myControl.setValue(v);
        }
    }

    change(e): void {
        this.value = !!e.target.checked;
    }

    get invalid(): boolean {
        if (this.ngControl) {
            return this.ngControl.invalid;
        }
        return false;
    }

    get valid(): boolean {
        if (this.ngControl) {
            return this.ngControl.valid;
        }
        return false;
    }

    get pristine(): boolean {
        if (this.ngControl) {
            return this.ngControl.pristine;
        }
        return false;
    }

    get dirty(): boolean {
        if (this.ngControl) {
            return this.ngControl.dirty;
        }
        return false;
    }

    get touched(): boolean {
        if (this.ngControl) {
            return this.ngControl.touched;
        }
        return false;
    }

    writeValue(value: any): void {
        this._value = value;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    registerOnChange(fn: any): void {
        // Store the provided function as an internal method.
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        // Store the provided function as an internal method.
        this.onTouched = fn;
    }
}
